import { useNavigate } from 'react-router-dom';
import usePermission from '../../hooks/usePermission';
import React, { useEffect } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
} from '@qlibs/react-components';
import RowFilter from '@qlibs/react-components/dist/Table/RowFilter';
import { Button, Image, Modal, Tag, message } from 'antd';
import { httpRequest } from '../../helpers/api';
import { EDateFormat } from '../../const/config';
import { AssetProperties } from '../asset/types/asset.type';
import LinkTable from '../../components/LinkTable';
import { DeviceTypeProperties } from './types/device.type';
import {
  formatCurrentDate,
  EFormatCurrentDate,
} from '../../helpers/formatCurrentDate';
const moment = require('moment');

const FEATURE = 'DEVICE_TYPE';

const Device = () => {
  const navigate = useNavigate();
  const { isUserHasPermission } = usePermission();

  const handleCreateDevice = () => {
    navigate({ pathname: '/device-type/add' });
  };

  const [isModalDeleteOpen, setIsModalDeleteOpen] = React.useState(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = React.useState(false);
  const [currentData, setCurrentData] = React.useState<DeviceTypeProperties>();
  const [dataAsset, setDataAsset] = React.useState<AssetProperties[]>([]);
  const [forceDeleteId, setForceDeleteId] = React.useState<string>();

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
  } = useFetchList<DeviceTypeProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'device-type',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      //
    },
    pageQuery: {
      defaultValue: {
        page: 1,
      },
    },
  });

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId && forceDeleteId === currentData.deviceTypeId;
        await httpRequest.delete('/device-type/' + currentData.deviceTypeId, {
          params: {
            forceDelete: forceDelete || undefined,
          },
        });
        message.success(
          `Success ${forceDelete ? 'delete' : 'archive'} ${currentData.name}`
        );

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        await httpRequest.patch(
          '/device-type/' + currentData.deviceTypeId + '/restore'
        );
        message.success(`Success restore ${currentData.name}`);

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleRestoreDevice(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find((item) => item.deviceTypeId === id);
    if (found) {
      setCurrentData(found);
    }
  }

  async function handleDeleteDevice(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find((item) => item.deviceTypeId === id);
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  function handleCancelDelete() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setCurrentData(undefined);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find(
        (item) => item.deviceTypeId === id && item.deletedAt
      );
      if (found) {
        return true;
      }
    }

    return false;
  }

  const columns: IAppTableColumn<DeviceTypeProperties>[] = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      type: 'detail',
      keyId: 'deviceTypeId',
      render: (value, records) => (
        <LinkTable
          onPress={() => navigate('/device-type/' + records.deviceTypeId)}
        >
          {records.deletedAt ? (
            <Tag color='#ff5500' className='mb-2'>
              Archived
            </Tag>
          ) : (
            false
          )}
          {value}
        </LinkTable>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'CREATED AT',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      type: 'actions',
      actions: [
        isUserHasPermission([FEATURE + '.DETAIL'])
          ? {
              key: 'detail',
              label: 'Detail',
              onPress: (id) => navigate('/device-type/' + id),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.UPDATE'])
          ? {
              key: 'update',
              label: 'Update',
              onPress: (id) => navigate('/device-type/' + id + '/edit'),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.DELETE'])
          ? {
              key: 'archive',
              label: 'Delete',
              color: '#ff4d4f',
              disabled: (id) => isArchived(id),
              disabledTooltip: 'This data is already archived',
              onPress: (id) => handleDeleteDevice(id),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.RESTORE'])
          ? {
              key: 'restore',
              label: 'Restore',
              color: '#1890ff',
              disabled: (id) => !isArchived(id),
              disabledTooltip: 'This data not in archived',
              onPress: (id) => handleRestoreDevice(id),
            }
          : undefined,
        isUserHasPermission([FEATURE + '.FORCE_DELETE'])
          ? {
              key: 'delete',
              label: 'Force Delete',
              onPress: (id) => handleDeleteDevice(id, true),
            }
          : undefined,
      ],
    },
  ];

  const filterData: any = [
    {
      type: 'search',
      key: 'search',
      label: 'Search',
      placeholder: 'Search by device type name',
      onChange: (value: any) => {
        handleSearch(value);
      },
      colSpan: isUserHasPermission([FEATURE + '.DELETE']) ? 6 : 10,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'all',
          label: 'All',
        },
        {
          value: 'active',
          label: 'Active',
        },
        {
          value: 'inactive',
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterStatus: value === 'all' ? '' : value,
        });
      },
      colSpan: 6,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 4,
    },
  ].filter((item) =>
    !isUserHasPermission([FEATURE + '.DELETE'])
      ? item.key !== 'filterData'
      : item
  );

  return (
    <React.Fragment>
      <HeaderSection
        title={'Device Type'}
        rightAction={[
          isUserHasPermission([FEATURE + '.CREATE']) ? (
            <Button type='primary' onClick={handleCreateDevice}>
              Add Device Type
            </Button>
          ) : (
            false
          ),
        ]}
      />

      <RowFilter
        filterValues={{
          search: pageQueries.search,
          status: pageQueries.status || 'all',
          filterData: pageQueries.filterData || 'default',
          filterAsset: pageQueries.filterAsset || 'all',
        }}
        filters={[filterData]}
      />

      <AppTable
        isLoading={isLoading}
        keyId='deviceTypeId'
        columns={columns}
        data={data}
        pagination={pagination}
        onChangePage={changePage}
        onChangeLimit={changeLimit}
        // _table={{
        //   scroll:{ x: '100%' }
        // } as any}
      />

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCancelDelete}
      >
        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            Are you sure want to delete this data? This data will be archived
            and only User with restore permission can undo this action.
          </p>
        )}
      </Modal>

      <Modal
        title='Restore Confirmation'
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCancelDelete}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>
    </React.Fragment>
  );
};
export default Device;
